import { Injectable } from '@angular/core';

import { MessageService } from './message.service';
import { OptionsService } from './options.service';
import { AnswerService } from './answer.service';
import { AuthService } from './auth.service';
import { CindiService } from './cindi.service';

@Injectable({
  providedIn: 'root',
})
export class CindiQuestionnaireService {
  private personalDataLoaded = false;
  personalData!: any;

  constructor(
    private messageService: MessageService,
    private optionsService: OptionsService,
    private answerService: AnswerService,
    private authService: AuthService,
    private cindiService: CindiService,
  ) {
    this.answerService.personalData$.subscribe((data: any) => {
      this.personalData = data;
    });
  }

  async initCindiQuestionnaire(idPessoa: number) {
    await this.answerService.createUserQuestionnaire(idPessoa);
    await this.messageService.addLocalBotMsgToChat(1);
    await this.messageService.addLocalBotMsgToChat(2);
    await this.messageService.addLocalBotMsgToChat(3);
  }

  async continueQuestionsFlow(nextMessageId: number) {
    const hasOtherSymptoms = this.answerService.hasOtherSymptoms;

    if (this.isInRange(nextMessageId, 78, 91) || nextMessageId > 106) {
      return;
    }

    if (nextMessageId == 57) {
      await this.optionsService.generateMessageWithOptions(57);
      return;
    }

    if (nextMessageId == 58 || nextMessageId == 59) {
      await this.handleIMCCalculation(nextMessageId);
    } else if (this.isInRange(nextMessageId, 60, 63)) {
      await this.handleIMCResults();
    } else if (this.isInRange(nextMessageId, 64, 75)) {
      this.handleAlimentaryHabitsMapping(nextMessageId);
    } else if (this.isInRange(nextMessageId, 76, 77)) {
      if (this.cindiService.resultadoIMC < 25) {
        this.handleIMCFeedbackLowIMC();
      } else {
        this.handlePsicoLaboralMapping(nextMessageId);
      }
    } else if (nextMessageId === 92) {
      this.handleTransteoricInstrument();
    } else if (nextMessageId === 106) {
      return hasOtherSymptoms
        ? this.handleSymptomFlow()
        : this.finishQuestionnaire();
    } else {
      if (!this.personalDataLoaded) {
        await this.getPersonalData();
        this.personalDataLoaded = true;
      }
      await this.handlePersonalDataQuestions();
    }
  }

  private isInRange(id: number, min: number, max: number): boolean {
    return id >= min && id <= max;
  }

  private async getPersonalData() {
    const userId = this.authService.getUserId();
    await this.answerService.loadPersonalDataFromApi(userId);
  }

  private async handlePersonalDataQuestions() {
    const fieldsInOrder = [
      { value: this.personalData.pais, questionId: 9 },
      { value: this.personalData.cidadeResidencia, questionId: 10 },
      { value: this.personalData.estadoCivil, questionId: 11 },
      { value: this.personalData.grauEscolaridade, questionId: 12 },
      { value: this.personalData.classificacaoEtnicoRacial, questionId: 13 },
      { value: this.personalData.cargo, questionId: 54 },
      { value: this.personalData.area, questionId: 55 },
      { value: this.personalData.turno, questionId: 16 },
    ];

    const next = fieldsInOrder.find(
      (item) => item.value === null || item.value === '',
    );

    if (next) {
      if (next.questionId == 16) {
        await this.optionsService.generateMessageWithOptions(
          next.questionId,
          undefined,
          1,
          2,
        );
      } else {
        await this.optionsService.generateMessageWithOptions(next.questionId);
      }
    } else {
      await this.optionsService.generateMessageWithOptions(56);
    }
  }

  private async handleIMCCalculation(id: number) {
    await this.optionsService.generateMessageWithOptions(id);
  }

  private async handleIMCResults() {
    await this.messageService.addLocalBotMsgToChat(60);
    await this.messageService.addLocalBotMsgToChat(61);
    await this.messageService.addLocalBotMsgToChat(62, 5000);
    await this.messageService.addLocalBotMsgToChat(63, 5000);
    await this.optionsService.generateMessageWithOptions(
      64,
      null,
      null,
      null,
      3000,
    );
  }

  private async handleAlimentaryHabitsMapping(id: number) {
    if (id == 73) {
      await this.messageService.addLocalBotMsgToChat(73);
      await this.optionsService.generateMessageWithOptions(
        74,
        null,
        null,
        null,
        3000,
      );
    } else {
      await this.optionsService.generateMessageWithOptions(id);
    }
  }

  private async handlePsicoLaboralMapping(id: number) {
    await this.optionsService.generateMessageWithOptions(id);

    if (id === 77) {
      if (this.cindiService.resultadoIMC >= 25) {
        await this.handleIMCFeedbackHighIMC();
      } else {
        await this.handleIMCFeedbackLowIMC();
      }
    }
  }

  private async handleIMCFeedbackLowIMC() {
    if (this.cindiService.pontuation <= 24) {
      await this.messageService.addLocalBotMsgToChat(82, 1000);
      await this.messageService.addLocalBotMsgToChat(83, 5000);
      await this.messageService.addLocalBotMsgToChat(84, 3000);
      await this.messageService.addLocalBotMsgToChat(85, 3000);
      await this.optionsService.generateMessageWithOptions(
        91,
        null,
        null,
        null,
        5000,
      );
    } else {
      await this.messageService.addLocalBotMsgToChat(86);
      await this.messageService.addLocalBotMsgToChat(87, 4000);
      await this.messageService.addLocalBotMsgToChat(88, 2000);
      await this.messageService.addLocalBotMsgToChat(89, 5000);
      await this.messageService.addLocalBotMsgToChat(90, 4000);
      await this.optionsService.generateMessageWithOptions(
        105,
        null,
        null,
        null,
        2000,
      );
    }
  }

  private async handleIMCFeedbackHighIMC() {
    await this.messageService.addLocalBotMsgToChat(78);
    await this.messageService.addLocalBotMsgToChat(79, 4000);
    await this.messageService.addLocalBotMsgToChat(80, 2000);
    await this.messageService.addLocalBotMsgToChat(81, 2000);
    await this.optionsService.generateMessageWithOptions(
      91,
      null,
      null,
      null,
      3000,
    );
  }

  private async handleTransteoricInstrument() {
    const availableToChanges = this.answerService.availableToChanges;

    if (availableToChanges) {
      await this.messageService.addLocalBotMsgToChat(92);
      await this.messageService.addLocalBotMsgToChat(93, 3000);
      await this.messageService.addLocalBotMsgToChat(94, 5000);
      await this.messageService.addLocalBotMsgToChat(95, 8000);
      await this.messageService.addLocalBotMsgToChat(96, 8000);
    } else {
      await this.messageService.addLocalBotMsgToChat(97);
      await this.messageService.addLocalBotMsgToChat(98, 2000);
      await this.messageService.addLocalBotMsgToChat(99, 3000);
      await this.messageService.addLocalBotMsgToChat(100, 3000);
      await this.messageService.addLocalBotMsgToChat(101, 5000);
      await this.messageService.addLocalBotMsgToChat(102, 5000);
      await this.messageService.addLocalBotMsgToChat(103, 5000);
    }
    await this.optionsService.generateMessageWithOptions(
      105,
      null,
      null,
      null,
      2000,
    );
  }

  private async handleSymptomFlow() {
    await this.optionsService.generateMessageWithOptions(106);
    await this.messageService.addLocalBotMsgToChat(107);
    await this.messageService.addLocalBotMsgToChat(108);
    await this.messageService.addLocalBotMsgToChat(109);
    await this.finishQuestionnaire();
  }

  async finishQuestionnaire() {
    await this.optionsService.generateMessageWithOptions(110);
    await this.messageService.addLocalBotMsgToChat(111);
    await this.messageService.addLocalBotMsgToChat(112);

    await this.answerService.saveAnswersByQuestionnaire();
    await this.answerService.setUserQuestionnaireStatus('Completo');
    this.logout();
  }

  logout() {
    setTimeout(() => {
      sessionStorage.removeItem('token');
      window.location.reload();
    }, 5000);
  }
}
